/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { type OnInit, Component, Injector, Input } from '@angular/core';
import { Validators } from '@angular/forms';
import { type Command, type Rest, type SlideConfig, DMValidators, FormControl, FormGroup, FormSlideWindowAbstract } from '@dm/core';
import { SelectOption } from '@dm/core/types';
import { ToolService } from 'app/common/services';
import { type UnixLocalUser } from 'app/common/types/mo';
import { SWITCH } from 'app/config/switch';

import { type UnixUserVm } from '../../../access-user';
import { AccessUserValidators } from '../../../access-user.validator';
import { AccessUserStrService } from '../../../access-user-str.service';
import {
    CreateUnixLocalAccessGroupComponent,
} from '../../unix-local-access-group/create-unix-local-access-group/create-unix-local-access-group.component';
import { UnixLocalAccessGroupService } from '../../unix-local-access-group/unix-local-access-group.service';
import { UnixLocalAccessUserService } from '../unix-local-access-user.service';

@Component({
    selector: 'app-create-unix-local-access-user',
    templateUrl: './create-unix-local-access-user.component.html',
    styleUrls: ['./create-unix-local-access-user.component.scss'],
})
export class CreateUnixLocalAccessUserComponent extends FormSlideWindowAbstract implements OnInit {
    @Input() vstore: SelectOption;
    i18n = this.str.i18n;
    closeArgs = { refresh: false };
    windowVisible = true;
    SWITCH = SWITCH;

    errorMessage: any;
    vm: FormGroup;

    get vmValue(): UnixUserVm {
        return this.vm ? this.vm.value : null;
    }

    /**
     * ID的范围， 0 - 4294967295
     */
    idRange = {
        min: 0,
        max: 4294967295,
    };

    // 主组选择器
    primaryGroupConfig: SlideConfig;

    // 多租户对象下拉列表选项
    vstoreRest: Rest;
    selectedVstore: SelectOption;

    constructor(
        private localUserService: UnixLocalAccessUserService,
        private localGroupService: UnixLocalAccessGroupService,
        private str: AccessUserStrService,
        injector: Injector,
        private toolService: ToolService,
    ) {
        super(injector);
    }

    ngOnInit(): void {
        this.primaryGroupConfig = {
            selectionMode: 'radio',
            width: '684px',
            title: this.i18n.selecetPrimaryGroupTitle,
            helpLink: 'authenticationuserCreate',
            min: 1,
            minTip: this.i18n.error.chooseOne,
            showCreate: true,
            createComponent: CreateUnixLocalAccessGroupComponent,
            componentParam: { vstore: this.vstore },
            rest: this.localGroupService.getBatchRest(),
            countRest: this.localGroupService.countRest(),
            columnDefs: [
                {
                    field: 'name',
                    header: this.i18n.name,
                    filterType: 'search',
                    sortable: false,
                },
                {
                    field: 'id',
                    header: this.i18n.id,
                    filterType: 'equalSearch',
                    sortable: true,
                },
            ],
        };

        this.vstoreRest = this.localUserService.getVstoreRest();

        super.ngOnInit();

        // 租户视图进入不再支持选租户，文本展示
        if (this.vstore && this.vstore.value) {
            this.selectedVstore = this.vstore;
            this.vm.get('vstoreItem').disable();
            this.primaryGroupConfig.rest = this.localGroupService.getBatchRest(this.vstore.value);
            this.primaryGroupConfig.countRest = this.localGroupService.countRest(this.vstore.value);
        } else {
            this.vm.get('vstoreItem').enable();
        }
    }

    vstoreItemChange({ option }: { option: SelectOption }): void {
        if (!this.selectedVstore || this.selectedVstore.value !== option.value) {
            this.selectedVstore = option;
            // 切换租户后清空主组数据
            this.vm.patchValue({ primary_group_name: undefined });
            // 设置必选错误提示
            const control = this.vm.get('primary_group_name');
            control.markAsTouched();
            control.markAsDirty();
            control.updateValueAndValidity();
            control.setErrors({ ...control.errors, required: true });
        }
    }

    canOpenSlide = (): boolean => {
        if (this.vstore && this.vstore.value) return true;

        const control = this.vm.get('vstoreItem');
        control.markAsTouched();
        control.markAsDirty();
        control.updateValueAndValidity();
        if (!control.value) {
            control.setErrors({ ...control.errors, required: true });
        } else {
            control.clearValidators();
        }
        if (control.value) {
            this.primaryGroupConfig.rest = this.localGroupService.getBatchRest(control.value);
            this.primaryGroupConfig.countRest = this.localGroupService.countRest(control.value);
        }
        return control.value;
    };

    initVm(): void {
        this.vm = new FormGroup({
            vstoreItem: new FormControl(null, {
                validators: [
                    Validators.required,
                ],
            }),
            name: new FormControl('', [
                Validators.required,
                DMValidators.rangeByteLength(1, 64),
                AccessUserValidators.accessName,
            ]),
            id: new FormControl(),
            primary_group_name: new FormControl(undefined, [
                Validators.required,
            ]),
            description: new FormControl('', [
                DMValidators.rangeByteLength(0, 255),
            ]),
        }, {
            updateOn: 'blur',
        });

        this.errorMessage = {
            vstoreItem: {
                required: this.toolService.requiredErr(this.i18n.vStoreLab),
            },
            name: {
                required: this.i18n.error.getRequired(this.i18n.name),
                rangeByteLength: this.toolService.rangeByteLengthErr(1, 64),
                accessName: this.i18n.error.accessName,
            },
            primary_group_name: {
                required: this.i18n.error.getRequired(this.i18n.mainGroup),
            },
            description: {
                rangeByteLength: this.toolService.rangeByteLengthErr(0, 255),
            },
        };
    }

    buildCmds(): Command[] {
        const cmds: Command[] = [this.buildCreateLocalUserCmd()];

        return cmds;
    }

    buildCreateLocalUserCmd(): Command {
        const vmValue = this.vmValue;
        const params: UnixLocalUser = {
            name: vmValue.name,
            description: vmValue.description,
            primary_group_name: vmValue.primary_group_name,
        };
        // 排除!0为true
        if (vmValue.id || vmValue.id === 0) {
            params.id = vmValue.id;
        }

        if (this.selectedVstore && this.selectedVstore.value) {
            params.vstoreId = this.selectedVstore.value;
        }

        return {
            name: this.i18n.rest.unixUser.create,
            nameParams: [params.name],
            action: this.localUserService.create(params),
        };
    }
}
